/**
 * Parsers des artefacts runner (references/final_lines/guardrails/params) en
 * « questions » lisibles + guidage d'étape. Extrait de TenderDetail.tsx (dette
 * front) — logique pure, sans dépendance React/UI.
 */

export type RunnerQuestion = {
  id: string;
  source: 'references' | 'guardrails' | 'final_lines' | 'params';
  path?: string;
  code?: string;
  title: string;
  message: string;
  table?: string | null;
  ref?: string | number | null;
  qty?: string | number | null;
  itemId?: string | null;
  lotId?: string | null;
};

// Guidage « étape → quoi faire » affiché en haut de la revue, en clair (le
// statut technique brut ne dit rien à un commercial).
export function stageGuidance(statut: string): { step: string; detail: string } | null {
  switch (statut) {
    case 'awaiting_params_review':
      return {
        step: 'Étape 1/3 · Paramètres',
        detail: 'Réponds aux questions générales ci-dessus (paramètres / périmètre de l’AO). Les lignes du devis seront générées après validation, puis « Recalculer ».',
      };
    case 'awaiting_references_review':
      return {
        step: 'Étape 2/3 · Références',
        detail: 'Corrige les lignes en attention directement dans le tableau du devis (clique le SKU « AUCUN » ou le ✏), puis « Recalculer avec mes corrections ».',
      };
    case 'awaiting_final_review':
      return {
        step: 'Étape 3/3 · Revue finale',
        detail: 'Vérifie chaque ligne, accepte (✓) ou corrige. Quand tout est bon, exporte l’Excel.',
      };
    case 'ready':
    case 'exported':
      return {
        step: 'Devis prêt',
        detail: 'Accepte / corrige les dernières lignes puis exporte. Ici une correction s’applique directement (sans recalcul).',
      };
    default:
      return null;
  }
}

// Traduit les notes techniques du resolver en motif lisible pour l'humain.
const RESOLVER_NOTE_LABELS: Record<string, string> = {
  capacity_param_missing: 'capacité non renseignée — impossible de dimensionner',
  capacity_unknown_for_capacitive_rule: 'capacité inconnue pour cette table capacitaire',
  no_numeric_capacity_in_table: 'aucune capacité numérique dans la table catalogue',
  table_empty: 'table catalogue vide',
  no_capacity_filter: 'pas de filtre de capacité',
};

export function humanizeResolverNotes(notes: unknown[]): string {
  const parts = notes
    .map(String)
    .map((n): string => {
      if (RESOLVER_NOTE_LABELS[n]) return RESOLVER_NOTE_LABELS[n];
      let m: RegExpMatchArray | null;
      if ((m = n.match(/^row_selection:(.+)$/))) {
        const inner = m[1];
        if (RESOLVER_NOTE_LABELS[inner]) return RESOLVER_NOTE_LABELS[inner];
        if ((m = inner.match(/^demand_exceeds_max_capacity:(.+)$/)))
          return `capacité demandée au-delà du maximum catalogue (${m[1]})`;
        if ((m = inner.match(/^no_exact_match_for_capacity:(.+)$/)))
          return `pas de capacité exacte au catalogue (${m[1]})`;
        return inner;
      }
      if ((m = n.match(/^variant_no_match_for:(.+)$/))) return `variante non disponible au catalogue (${m[1]})`;
      if ((m = n.match(/^variant_params_missing:\[?(.+?)\]?$/))) return `paramètres de variante manquants (${m[1]})`;
      if ((m = n.match(/^empty_cell_at:(.+)$/))) return `référence absente de la cellule catalogue (${m[1]})`;
      if (n.startsWith('capacity_via_fallback')) return 'capacité estimée par formule (valeur explicite absente de l’AO)';
      if (n.startsWith('not_applicable') || n.startsWith('quantity_') || n.startsWith('export:')) return '';
      return n;
    })
    .filter(Boolean);
  return parts.join(' ; ');
}

export function refKeyOf(table?: string | null, itemId?: string | null, lotId?: string | null): string {
  return `${(table ?? '').toLowerCase()}|${itemId ?? ''}|${lotId ?? ''}`;
}

export function extractRunnerQuestions(input: {
  params: Record<string, unknown> | null;
  references: Record<string, unknown> | null;
  guardrails: Record<string, unknown> | null;
  finalLines: Record<string, unknown> | null;
}): RunnerQuestion[] {
  const questions: RunnerQuestion[] = [];
  // Points références déjà représentés par une carte détaillée (results[]),
  // pour éviter les doublons (résumé needs_attention + guardrail).
  const coveredRef = new Set<string>();

  for (const q of asArray(input.params?.questions)) {
    if (!isRecord(q)) continue;
    questions.push({
      id: stableQuestionId('params', questions.length, q.id, q.path),
      source: 'params',
      path: asString(q.path) ?? undefined,
      code: asString(q.type ?? q.code) ?? undefined,
      title: asString(q.question ?? q.title) ?? 'Question sur les parametres extraits',
      message: asString(q.message ?? q.line ?? q.reason) ?? JSON.stringify(q),
      ref: asPrimitive(q.ref),
      itemId: asString(q.item_id),
      lotId: asString(q.lot_id),
    });
  }

  // references.results — AUTORITAIRE (notes détaillées) : traité EN PREMIER.
  asArray(input.references?.results).forEach((r, index) => {
    if (!isRecord(r) || r.needs_attention !== true) return;
    coveredRef.add(refKeyOf(asString(r.table), asString(r.item_id), asString(r.lot_id)));
    const msg = humanizeResolverNotes(asArray(r.notes));
    const proposed = asPrimitive(r.ref);
    const hasRef = proposed != null && String(proposed).trim() !== '' && String(proposed).trim() !== '—';
    const fallbackMsg = hasRef
      ? `La plateforme propose ${proposed} mais n'a pas pu confirmer le dimensionnement (donnée d'entrée absente de l'AO). Confirme ce SKU s'il convient (bouton « Sauver réponse »), sinon corrige-le dans le champ REF/SKU.`
      : 'Aucune référence trouvée pour cette ligne — renseigne le SKU catalogue (et une quantité si besoin), puis « Sauver réponse ».';
    questions.push({
      id: stableQuestionId('references', questions.length, r.table, `references.results.${index}`),
      source: 'references',
      path: `references.results.${index}`,
      code: 'reference_needs_attention',
      title: questionTitle('reference_needs_attention', asString(r.table), asString(r.ref)),
      message: msg || fallbackMsg,
      table: asString(r.table),
      ref: asPrimitive(r.ref),
      qty: asPrimitive(r.quantity),
      itemId: asString(r.item_id),
      lotId: asString(r.lot_id),
    });
  });

  // references.needs_attention (résumé) : seulement si pas déjà couvert, et avec
  // un message LISIBLE (plus de JSON brut).
  for (const q of asArray(input.references?.needs_attention)) {
    if (!isRecord(q)) continue;
    const k = refKeyOf(asString(q.table), asString(q.item_id), asString(q.lot_id));
    if (coveredRef.has(k)) continue;
    coveredRef.add(k);
    const msg = asString(q.message ?? q.reason ?? q.question) ?? humanizeResolverNotes(asArray(q.notes));
    const proposedQ = asPrimitive(q.ref);
    const hasRefQ = proposedQ != null && String(proposedQ).trim() !== '' && String(proposedQ).trim() !== '—';
    const fallbackMsgQ = hasRefQ
      ? `La plateforme propose ${proposedQ} à confirmer (dimensionnement incertain, donnée absente de l'AO) — confirme ou corrige le SKU.`
      : 'Référence à renseigner (SKU catalogue).';
    questions.push({
      id: stableQuestionId('references', questions.length, q.code ?? q.table, q.path ?? k),
      source: 'references',
      path: asString(q.path) ?? undefined,
      code: asString(q.code ?? q.type) ?? undefined,
      title: questionTitle(asString(q.code), asString(q.table), asString(q.ref)),
      message: msg || fallbackMsgQ,
      table: asString(q.table),
      ref: asPrimitive(q.ref),
      itemId: asString(q.item_id),
      lotId: asString(q.lot_id),
    });
  }

  for (const issue of asArray(input.guardrails?.issues)) {
    if (!isRecord(issue) || issue.severity !== 'blocking') continue;
    const code = asString(issue.code);
    // On saute le guardrail « reference_needs_attention » déjà couvert par une
    // carte results[] (même table|item|lot).
    if (code === 'reference_needs_attention' && coveredRef.has(refKeyOf(asString(issue.table), asString(issue.itemId), asString(issue.lotId)))) {
      continue;
    }
    const id = stableQuestionId('guardrails', questions.length, issue.code, issue.path);
    if (questions.some((q) => q.id === id || (q.path && q.path === issue.path && q.code === code))) continue;
    questions.push({
      id,
      source: 'guardrails',
      path: asString(issue.path) ?? undefined,
      code: code ?? undefined,
      title: questionTitle(code, asString(issue.table), asString(issue.ref)),
      message: asString(issue.message) ?? 'Point bloquant TenderFlow a clarifier.',
      table: asString(issue.table),
      ref: asPrimitive(issue.ref),
      itemId: asString(issue.itemId),
      lotId: asString(issue.lotId),
    });
  }

  asArray(input.finalLines?.lines).forEach((line, index) => {
    if (!isRecord(line)) return;
    if (line.status !== 'needs_attention' && !line.internal_alert) return;
    questions.push({
      id: stableQuestionId('final_lines', questions.length, line.ref, `final_lines.lines.${index}`),
      source: 'final_lines',
      path: `final_lines.lines.${index}`,
      code: 'final_line_needs_attention',
      title: asString(line.designation ?? line.category ?? line.ref) ?? 'Ligne finale a revoir',
      message: asString(line.internal_alert ?? line.commercial_note) ?? 'Validation finale requise.',
      table: asString(line.source_table),
      ref: asPrimitive(line.ref),
      itemId: asString(line.item_id),
      lotId: asString(line.lot_id),
    });
  });

  const seen = new Set<string>();
  const seenProduct = new Set<string>();
  return questions.filter((q) => {
    // 1) dédup technique : source + path + message identiques.
    const key = `${q.source}:${q.path ?? ''}:${q.code ?? ''}:${q.message}`;
    if (seen.has(key)) return false;
    seen.add(key);
    // 2) dédup par IDENTITÉ PRODUIT : la même (table, réf) signalée sous plusieurs
    //    scopes internes du résolveur (items réels + réconciliation auto_*) ne doit
    //    apparaître qu'UNE fois — sinon le compteur « à traiter » sur-compte (ex. 54)
    //    par rapport aux lignes réelles du devis (ex. 23). Réf absente -> on
    //    retombe sur (table, item) pour ne pas fusionner des lignes distinctes.
    const refStr = q.ref != null ? String(q.ref).trim() : '';
    const productKey = refStr
      ? `p:${(q.table ?? '').toLowerCase()}|${refStr.toLowerCase()}|${q.lotId ?? ''}`
      : `p:${(q.table ?? '').toLowerCase()}|${q.itemId ?? ''}|${q.lotId ?? ''}`;
    if (seenProduct.has(productKey)) return false;
    seenProduct.add(productKey);
    return true;
  });
}

export function questionTitle(code?: string | null, table?: string | null, ref?: string | null): string {
  if (code === 'resolved_table_outside_requested_family') return `Famille incompatible${table ? `: ${table}` : ''}`;
  if (code === 'sku_not_in_runner_catalogue' || code === 'final_sku_not_in_runner_catalogue') {
    return `SKU absent du catalogue${ref ? `: ${ref}` : ''}`;
  }
  if (code === 'family_unknown_in_catalogue') return 'Famille absente du catalogue';
  if (code === 'reference_needs_attention') return `Reference a confirmer${table ? `: ${table}` : ''}`;
  return table ? `Question catalogue: ${table}` : 'Question runner';
}

export function stableQuestionId(source: string, index: number, a?: unknown, b?: unknown): string {
  return `${source}_${String(a ?? 'q').replace(/[^a-z0-9_-]+/gi, '_')}_${String(b ?? index).replace(/[^a-z0-9_-]+/gi, '_')}`;
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

export function asString(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

export function asPrimitive(value: unknown): string | number | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  return null;
}
